import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { UserProfileManager } from '../utils/userProfile'
import { GoFishAI } from '../utils/ai/gofish'
import { GameSetupScreen } from '../components/GameSetupScreen'
import { GameHeader } from '../components/GameHeader'
import { GameRoomHeader, GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/GoFish.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K'
type Suit = '♠' | '♥' | '♦' | '♣'
type Card = { rank: Rank; suit: Suit }

type GameMode = 'select' | 'local' | 'network' | 'ai'
type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'finished'
type AIDifficulty = 'easy' | 'medium' | 'hard'

type Notification = {
  message: string
  type: 'info' | 'success' | 'error'
}

type Player = {
  id: number
  name: string
  hand: Card[]
  books: Rank[]
}

const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
const SUITS: Suit[] = ['♠', '♥', '♦', '♣']

export default function GoFish() {
  const [searchParams] = useSearchParams()
  const joinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const navigate = useNavigate()
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')

  // AI instance
  const aiInstanceRef = useRef<GoFishAI | null>(null)

  // Local game state
  const [playerCount, setPlayerCount] = useState<2 | 3 | 4>(2)
  const [players, setPlayers] = useState<Player[]>([])
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0)
  const [deck, setDeck] = useState<Card[]>([])
  const [winner, setWinner] = useState<Player | null>(null)
  const [askingRank, setAskingRank] = useState<Rank | null>(null)

  // Network game state
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [sessionCode, setSessionCode] = useState<string | undefined>(undefined)

  // Notification state
  const [notification, setNotification] = useState<Notification | null>(null)
  const [gameLog, setGameLog] = useState<string[]>([])
  const [logExpanded, setLogExpanded] = useState(false)

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  const {
    session,
    loading,
    error,
    createSession,
    joinSession,
    sendMove,
    onMessage,
  } = useEnhancedGameSession({
    sessionCode,
    playerNumber,
    playerName,
  })

  // Auto-join if join code in URL — go to setup so user can enter name
  useEffect(() => {
    if (joinCode && phase === 'select') {
      setGameMode('network')
      setPhase('setup')
    }
  }, [joinCode, phase])

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      const state = data.state
      if (state.players) setPlayers(state.players)
      if (state.deck) setDeck(state.deck)
      if (state.currentPlayerIndex !== undefined) setCurrentPlayerIndex(state.currentPlayerIndex)
      if (state.gameLog) setGameLog(state.gameLog)
      if (state.winner !== undefined) {
        setWinner(state.winner)
        if (state.winner) setPhase('finished')
      }
    })

    return () => unsubscribe()
  }, [gameMode, onMessage])

  const showNotification = (message: string, type: Notification['type'] = 'info') => {
    setNotification({ message, type })
  }

  const addLog = (message: string) => {
    setGameLog(prev => [...prev, message].slice(-10)) // Keep last 10 messages
  }

  const handleModeSelect = (mode: GameMode) => {
    setGameMode(mode)
    setPhase('setup')
  }

  const createDeck = (): Card[] => {
    const deck: Card[] = []
    for (const rank of RANKS) {
      for (const suit of SUITS) {
        deck.push({ rank, suit })
      }
    }
    return shuffleDeck(deck)
  }

  const shuffleDeck = (deck: Card[]): Card[] => {
    const shuffled = [...deck]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  const dealCards = (numPlayers: number): { players: Player[]; deck: Card[] } => {
    const newDeck = createDeck()
    const cardsPerPlayer = numPlayers === 2 ? 7 : 5
    const newPlayers: Player[] = []

    for (let i = 0; i < numPlayers; i++) {
      const hand = newDeck.splice(0, cardsPerPlayer)
      newPlayers.push({
        id: i,
        name: `Player ${i + 1}`,
        hand,
        books: []
      })
    }

    return { players: newPlayers, deck: newDeck }
  }

  const checkForBooks = (player: Player): { player: Player; newBooks: Rank[] } => {
    const rankCounts: { [key in Rank]?: number } = {}
    player.hand.forEach(card => {
      rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1
    })

    const newBooks: Rank[] = []
    Object.entries(rankCounts).forEach(([rank, count]) => {
      if (count === 4) {
        newBooks.push(rank as Rank)
        player.hand = player.hand.filter(card => card.rank !== rank)
        player.books.push(rank as Rank)
      }
    })

    return { player, newBooks }
  }

  const startLocalGame = () => {
    const { players: newPlayers, deck: newDeck } = dealCards(playerCount)

    // Update player names
    newPlayers.forEach((player, index) => {
      const nameInput = document.getElementById(`player-${index}-name`) as HTMLInputElement
      if (nameInput && nameInput.value.trim()) {
        player.name = nameInput.value.trim()
      }
    })

    // Check for initial books
    const initialLog: string[] = []
    newPlayers.forEach(player => {
      const result = checkForBooks(player)
      if (result.newBooks.length > 0) {
        const msg = `${player.name} starts with ${result.newBooks.length} book(s)!`
        initialLog.push(msg)
      }
    })
    initialLog.push('Game started!')

    setPlayers(newPlayers)
    setDeck(newDeck)
    setCurrentPlayerIndex(0)
    setPhase('playing')
    setGameLog(initialLog)

    // Send initial state if network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players: newPlayers,
        deck: newDeck,
        currentPlayerIndex: 0,
        gameLog: initialLog,
      }})
    }
  }

  const askForCard = (targetPlayerIndex: number, rank: Rank) => {
    const currentPlayer = players[currentPlayerIndex]
    const targetPlayer = players[targetPlayerIndex]

    // Check if current player has the rank
    if (!currentPlayer.hand.some(card => card.rank === rank)) {
      showNotification("You don't have that rank in your hand!", 'error')
      return
    }

    // Find matching cards
    const matchingCards = targetPlayer.hand.filter(card => card.rank === rank)

    if (matchingCards.length > 0) {
      // Transfer cards
      const newPlayers = [...players]
      newPlayers[targetPlayerIndex].hand = targetPlayer.hand.filter(card => card.rank !== rank)
      newPlayers[currentPlayerIndex].hand.push(...matchingCards)

      // Check for books
      const bookResult = checkForBooks(newPlayers[currentPlayerIndex])
      if (bookResult.newBooks.length > 0) {
        addLog(`${currentPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
        showNotification(`You made a book of ${rank}s!`, 'success')
      }

      // Refill target's empty hand from deck (Go Fish rules)
      let currentDeck = deck
      if (newPlayers[targetPlayerIndex].hand.length === 0 && currentDeck.length > 0) {
        newPlayers[targetPlayerIndex].hand.push(currentDeck[0])
        currentDeck = currentDeck.slice(1)
        addLog(`${targetPlayer.name} drew a card (empty hand)`)
        setDeck(currentDeck)
      }

      setPlayers(newPlayers)
      const logMsg = `${currentPlayer.name} got ${matchingCards.length} ${rank}(s) from ${targetPlayer.name}`
      addLog(logMsg)

      // Send state update
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players: newPlayers,
          deck: currentDeck,
          currentPlayerIndex,
          gameLog: [...gameLog, logMsg],
        }})
      }

      // Same player goes again
      setAskingRank(null)
    } else {
      // Go fish!
      const logMsg = `${targetPlayer.name} said "Go Fish!"`
      addLog(logMsg)
      goFish()
    }

    checkWinCondition()
  }

  const goFish = () => {
    if (deck.length === 0) {
      nextTurn()
      return
    }

    const drawnCard = deck[0]
    const newDeck = deck.slice(1)
    const newPlayers = [...players]
    newPlayers[currentPlayerIndex].hand.push(drawnCard)

    addLog(`${players[currentPlayerIndex].name} drew a card`)

    // Check for books
    const bookResult = checkForBooks(newPlayers[currentPlayerIndex])
    if (bookResult.newBooks.length > 0) {
      addLog(`${players[currentPlayerIndex].name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
      showNotification(`You made a book!`, 'success')
    }

    setPlayers(newPlayers)
    setDeck(newDeck)

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players: newPlayers,
        deck: newDeck,
        currentPlayerIndex,
        gameLog,
      }})
    }

    // If the drawn card matches the asked rank, player goes again
    // Check BEFORE clearing askingRank
    if (askingRank && drawnCard.rank === askingRank) {
      showNotification('Lucky! You drew the card you asked for. Go again!', 'success')
      setAskingRank(null)
      // Don't call nextTurn() - player goes again
    } else {
      setAskingRank(null)
      nextTurn()
    }
  }

  const nextTurn = () => {
    let nextIndex = (currentPlayerIndex + 1) % players.length

    // Skip players with no cards
    let attempts = 0
    while (players[nextIndex].hand.length === 0 && attempts < players.length) {
      nextIndex = (nextIndex + 1) % players.length
      attempts++
    }

    setCurrentPlayerIndex(nextIndex)
    setAskingRank(null)

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players,
        deck,
        currentPlayerIndex: nextIndex,
        gameLog,
      }})
    }
  }

  const checkWinCondition = () => {
    // Game ends when all books are made (13 books total)
    const totalBooks = players.reduce((sum, player) => sum + player.books.length, 0)

    if (totalBooks === 13 || deck.length === 0 && players.every(p => p.hand.length === 0)) {
      const winner = players.reduce((max, player) =>
        player.books.length > max.books.length ? player : max
      )
      setWinner(winner)
      setPhase('finished')
      showNotification(`${winner.name} wins with ${winner.books.length} books!`, 'success')

      // Send final state update
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players,
          deck,
          currentPlayerIndex,
          gameLog,
          winner,
        }})
      }
    }
  }

  // AI turn logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentPlayerIndex !== 1) return // AI is player 2 (index 1)
    if (!aiInstanceRef.current) return
    if (players.length < 2) return

    const aiPlayer = players[1]
    if (aiPlayer.hand.length === 0) {
      // AI has no cards, skip turn
      nextTurn()
      return
    }

    // Update AI's hand
    aiInstanceRef.current.setHand(aiPlayer.hand)

    // AI makes a decision after a delay
    const delay = aiDifficulty === 'easy' ? 1500 : aiDifficulty === 'medium' ? 1200 : 800
    const timer = setTimeout(() => {
      if (!aiInstanceRef.current) return

      const rankToAsk = aiInstanceRef.current.chooseRankToAsk()
      const humanPlayer = players[0]

      addLog(`${aiPlayer.name} asks for ${rankToAsk}s`)
      showNotification(`AI asks: Do you have any ${rankToAsk}s?`, 'info')

      // Check if human has the rank
      const matchingCards = humanPlayer.hand.filter(card => card.rank === rankToAsk)

      if (matchingCards.length > 0) {
        // Human has the cards - transfer them
        const newPlayers = [...players]
        newPlayers[0].hand = humanPlayer.hand.filter(card => card.rank !== rankToAsk)
        newPlayers[1].hand.push(...matchingCards)

        // AI records successful ask
        aiInstanceRef.current.recordOpponentResponse(rankToAsk, true, matchingCards.length)

        // Check for books
        const bookResult = checkForBooks(newPlayers[1])
        if (bookResult.newBooks.length > 0) {
          addLog(`${aiPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
          bookResult.newBooks.forEach(rank => {
            aiInstanceRef.current?.recordOpponentBook(rank)
          })
        }

        // Refill human's empty hand from deck (Go Fish rules)
        let currentDeck = deck
        if (newPlayers[0].hand.length === 0 && currentDeck.length > 0) {
          newPlayers[0].hand.push(currentDeck[0])
          currentDeck = currentDeck.slice(1)
          addLog(`${humanPlayer.name} drew a card (empty hand)`)
          setDeck(currentDeck)
        }

        setPlayers(newPlayers)
        addLog(`${aiPlayer.name} got ${matchingCards.length} ${rankToAsk}(s) from ${humanPlayer.name}`)
        checkWinCondition()
        // AI goes again - don't call nextTurn()
      } else {
        // Human doesn't have it - Go Fish!
        aiInstanceRef.current.recordOpponentResponse(rankToAsk, false)
        addLog(`${humanPlayer.name} said "Go Fish!"`)

        if (deck.length === 0) {
          nextTurn()
          return
        }

        const drawnCard = deck[0]
        const newDeck = deck.slice(1)
        const newPlayers = [...players]
        newPlayers[1].hand.push(drawnCard)

        addLog(`${aiPlayer.name} drew a card`)

        // Check for books
        const bookResult = checkForBooks(newPlayers[1])
        if (bookResult.newBooks.length > 0) {
          addLog(`${aiPlayer.name} completed ${bookResult.newBooks.length} book(s): ${bookResult.newBooks.join(', ')}!`)
          bookResult.newBooks.forEach(rank => {
            aiInstanceRef.current?.recordOpponentBook(rank)
          })
        }

        setPlayers(newPlayers)
        setDeck(newDeck)

        checkWinCondition()

        // If drew the asked card, AI goes again
        if (drawnCard.rank === rankToAsk) {
          addLog(`${aiPlayer.name} drew the requested ${rankToAsk}!`)
          // AI goes again - don't call nextTurn()
        } else {
          nextTurn()
        }
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentPlayerIndex, players, deck, aiDifficulty])

  // Record opponent asks when human asks AI (for AI learning)
  const askForCardWithAI = (targetPlayerIndex: number, rank: Rank) => {
    const currentPlayer = players[currentPlayerIndex]
    const targetPlayer = players[targetPlayerIndex]

    // Check if current player has the rank
    if (!currentPlayer.hand.some(card => card.rank === rank)) {
      showNotification("You don't have that rank in your hand!", 'error')
      return
    }

    // If human is asking AI, record it
    if (gameMode === 'ai' && targetPlayerIndex === 1 && aiInstanceRef.current) {
      const matchingCards = targetPlayer.hand.filter(card => card.rank === rank)
      aiInstanceRef.current.recordOpponentAsk(rank, matchingCards.length > 0)
    }

    // Continue with normal logic
    askForCard(targetPlayerIndex, rank)
  }

  const handleCreateNetworkGame = async () => {
    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      const newSession = await createSession('gofish', playerName.trim())
      setSessionCode(newSession.session_code)
      setPlayerNumber(1)
      setPhase('lobby')
      showNotification(`Game created! Share code: ${newSession.session_code}`, 'success')
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to create game', 'error')
    }
  }

  const handleJoinNetworkGame = async (code?: string) => {
    const targetCode = code || joinCode
    if (!targetCode) {
      showNotification('Please enter a session code', 'error')
      return
    }

    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      await joinSession(targetCode, playerName.trim())
      setSessionCode(targetCode)
      setPlayerNumber(2)
      setPhase('playing')
      showNotification('Joined game successfully!', 'success')
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to join game', 'error')
    }
  }

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode === 'network' && sessionCode) {
      // Network mode - send via session
      await sendMove({ type: 'chat', message })

      // Add to local chat messages
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }, [gameMode, sessionCode, sendMove])

  // Watch for player 2 joining (player 1 is in lobby phase waiting)
  useEffect(() => {
    if (session && phase === 'lobby' && session.player2_name) {
      setPhase('playing')
      showNotification(`${session.player2_name} joined!`, 'success')

      // Player 1 initializes the game
      if (playerNumber === 1) {
        const { players: newPlayers, deck: newDeck } = dealCards(2)
        newPlayers[0].name = session.player1_name
        newPlayers[1].name = session.player2_name

        // Check for initial books
        const initialLog: string[] = []
        newPlayers.forEach(player => {
          const result = checkForBooks(player)
          if (result.newBooks.length > 0) {
            const msg = `${player.name} starts with ${result.newBooks.length} book(s)!`
            initialLog.push(msg)
          }
        })
        initialLog.push('Game started!')

        setPlayers(newPlayers)
        setDeck(newDeck)
        setCurrentPlayerIndex(0)
        setGameLog(initialLog)

        // Broadcast initial state to player 2
        sendMove({ type: 'game_state', state: {
          players: newPlayers,
          deck: newDeck,
          currentPlayerIndex: 0,
          gameLog: initialLog,
        }})
      }
    }
  }, [session, phase, playerNumber, sendMove])

  // Handle real-time WebRTC messages for chat
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribeChat = onMessage('chat', (data) => {
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  const NotificationComponent = () => {
    if (!notification) return null

    return (
      <div className={`notification notification-${notification.type}`}>
        {notification.message}
        <button className="notification-close" onClick={() => setNotification(null)}>×</button>
      </div>
    )
  }

  const getRankCards = (hand: Card[]): Map<Rank, Card[]> => {
    const rankMap = new Map<Rank, Card[]>()
    hand.forEach(card => {
      if (!rankMap.has(card.rank)) {
        rankMap.set(card.rank, [])
      }
      rankMap.get(card.rank)!.push(card)
    })
    return rankMap
  }

  // Mode selection — uses shared GameSetupScreen component
  if (phase === 'select') {
    return (
      <GameSetupScreen
        gameTitle="Go Fish"
        gameIcon="🎣"
        gameBanner="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        supportedModes={['ai', 'local', 'network']}
        onModeSelect={handleModeSelect}
        onBack={() => navigate('/')}
      />
    )
  }

  // Setup screen — handles ai, local, and network modes
  if (phase === 'setup') {
    return (
      <div className="gofish-container">
        <NotificationComponent />
        <GameHeader
          gameTitle="Go Fish"
          gameIcon="🎣"
          players={[]}
          showBackButton={true}
          voiceControls={undefined}
        />
        <div className="arcade-setup-screen">
          <div className="arcade-setup-card">
            <h2>
              {gameMode === 'ai' ? '🤖 vs AI' : gameMode === 'network' ? '🌐 Online Game' : '🏠 Local Game'}
            </h2>

            <div className="setup-form">
              {/* Name input — network mode only */}
              {gameMode === 'network' && (
                <div className="input-group">
                  <label htmlFor="player-name">Your Name</label>
                  <input
                    type="text"
                    id="player-name"
                    className="input"
                    placeholder="Enter your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    maxLength={20}
                    autoFocus
                    autoComplete="off"
                  />
                </div>
              )}

              {/* AI difficulty */}
              {gameMode === 'ai' && (
                <div className="input-group">
                  <label>AI Difficulty</label>
                  <div className="difficulty-selector">
                    <button className={`difficulty-btn ${aiDifficulty === 'easy' ? 'active' : ''}`} onClick={() => setAiDifficulty('easy')}>😊 Easy</button>
                    <button className={`difficulty-btn ${aiDifficulty === 'medium' ? 'active' : ''}`} onClick={() => setAiDifficulty('medium')}>😐 Medium</button>
                    <button className={`difficulty-btn ${aiDifficulty === 'hard' ? 'active' : ''}`} onClick={() => setAiDifficulty('hard')}>😤 Hard</button>
                  </div>
                </div>
              )}

              {/* Local: player count */}
              {gameMode === 'local' && (
                <div className="input-group">
                  <label>Number of Players</label>
                  <div className="difficulty-selector">
                    {([2, 3, 4] as const).map(n => (
                      <button key={n} className={`difficulty-btn ${playerCount === n ? 'active' : ''}`} onClick={() => setPlayerCount(n)}>
                        {n} Players
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Local: player names */}
              {gameMode === 'local' && (
                <div className="input-group">
                  <label>Player Names</label>
                  {Array(playerCount).fill(null).map((_, i) => (
                    <input
                      key={i}
                      id={`player-${i}-name`}
                      type="text"
                      className="input"
                      placeholder={`Player ${i + 1}`}
                      defaultValue={`Player ${i + 1}`}
                      style={{ marginBottom: i < playerCount - 1 ? 8 : 0 }}
                    />
                  ))}
                </div>
              )}

              <div className="setup-actions">
                {/* AI: start game */}
                {gameMode === 'ai' && (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() => {
                      aiInstanceRef.current = new GoFishAI()
                      const { players: newPlayers, deck: newDeck } = dealCards(2)
                      newPlayers[0].name = userProfile?.name || 'You'
                      newPlayers[1].name = `AI (${aiDifficulty})`
                      const initialLog: string[] = []
                      newPlayers.forEach(player => {
                        const result = checkForBooks(player)
                        if (result.newBooks.length > 0) {
                          initialLog.push(`${player.name} starts with ${result.newBooks.length} book(s)!`)
                        }
                      })
                      initialLog.push('Game started!')
                      aiInstanceRef.current.setHand(newPlayers[1].hand)
                      setPlayers(newPlayers)
                      setDeck(newDeck)
                      setCurrentPlayerIndex(0)
                      setPhase('playing')
                      setGameLog(initialLog)
                    }}
                  >
                    🎮 Start Game
                  </button>
                )}

                {/* Local: start game */}
                {gameMode === 'local' && (
                  <button className="btn btn-primary btn-lg" onClick={startLocalGame}>
                    🎮 Start Game
                  </button>
                )}

                {/* Network: create or join */}
                {gameMode === 'network' && (
                  <>
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleCreateNetworkGame}
                      disabled={loading || !playerName.trim()}
                    >
                      {loading ? '⏳ Creating...' : '➕ Create Game'}
                    </button>

                    <div className="divider-text">or</div>

                    <div className="join-section">
                      <input
                        type="text"
                        id="join-code-input"
                        className="input"
                        placeholder="Enter 6-char code"
                        defaultValue={joinCode || ''}
                        maxLength={6}
                        style={{ textTransform: 'uppercase', flex: 1 }}
                        autoCapitalize="characters"
                        autoComplete="off"
                      />
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          const input = document.getElementById('join-code-input') as HTMLInputElement
                          handleJoinNetworkGame(input.value.toUpperCase())
                        }}
                        disabled={loading || !playerName.trim()}
                      >
                        Join
                      </button>
                    </div>
                  </>
                )}

                <button className="btn btn-secondary" onClick={() => { setGameMode('select'); setPhase('select') }}>
                  ← Back
                </button>
              </div>

              {error && (
                <div style={{ padding: '10px 14px', background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 8, color: '#ff8080', fontSize: 13 }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }



  // Lobby — player 1 waiting for player 2 to join
  if (gameMode === 'network' && phase === 'lobby' && !session?.player2_name) {
    const joinUrl = `${window.location.origin}/gofish?join=${session?.session_code}`
    return (
      <div className="gofish-container">
        <NotificationComponent />
        <GameHeader
          gameTitle="Go Fish"
          gameIcon="🎣"
          players={[
            { number: 1, name: session?.player1_name || playerName, isConnected: true, avatar: '🎣' },
            { number: 2, name: 'Waiting...', isConnected: false, avatar: '?' }
          ]}
          sessionCode={session?.session_code}
        />
        <div className="arcade-lobby-screen">
          <div className="arcade-lobby-content">
            <div className="arcade-lobby-banner">
              <h2>Waiting for Player 2...</h2>
              <p className="gofish-session-code-display">
                Share code: <strong className="gofish-session-code-big">{session?.session_code}</strong>
              </p>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(joinUrl)
                  showNotification('Join link copied!', 'success')
                }}
              >
                📋 Copy Join Link
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setPhase('setup')}
                style={{ marginTop: 'var(--space-md)' }}
              >
                ← Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Playing
  if (phase === 'playing') {
    // Player 2 joined but hasn't received game state yet
    if (players.length === 0) {
      return (
        <div className="gofish-container">
          <GameHeader
            gameTitle="Go Fish"
            gameIcon="🎣"
            players={[]}
            showBackButton={true}
            voiceControls={undefined}
          />
          <div className="arcade-lobby-screen">
            <div className="arcade-lobby-content">
              <div className="arcade-lobby-banner">
                <h2>Starting game...</h2>
                <p style={{ color: 'var(--text-secondary)' }}>Waiting for host to deal cards</p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    const currentPlayer = players[currentPlayerIndex]

    // In network mode, determine if it's this player's turn
    const isMyTurn = gameMode === 'network'
      ? currentPlayerIndex === (playerNumber - 1)
      : true // Local/AI mode always shows current player controls

    // In network mode, show own player's hand, not current player's
    const myPlayerIndex = gameMode === 'network' ? (playerNumber - 1) : currentPlayerIndex
    const myPlayer = players[myPlayerIndex] || currentPlayer
    const rankMap = getRankCards(myPlayer.hand)
    const availableRanks = Array.from(rankMap.keys())

    return (
      <div className="gofish-container universal-game-room">
          <NotificationComponent />

          {/* Universal Header */}
          <GameRoomHeader
            gameTitle="Go Fish"
            gameIcon="🎣"
            mode={gameMode as 'ai' | 'network' | 'local'}
            players={players.map((player, idx) => ({
              name: player.name,
              status: idx === currentPlayerIndex ? 'active' : undefined
            }))}
            sessionCode={session?.session_code}
            onBack={() => window.location.href = '/'}
            compact={true}
          />

          <div className="game-room-content">
            {/* Main game board */}
            <div className="game-board-area">
              <div className="game-board">
                <div className="game-status">
                  <div className="deck-info">
                    <div className="deck-pile">
                      <span className="deck-count">{deck.length}</span>
                      <span>cards left</span>
                    </div>
                  </div>

                  <div className="current-turn">
                    <span className="turn-label">Current Turn:</span>
                    <span className="player-name">{currentPlayer.name}</span>
                    {gameMode === 'network' && (
                      <span style={{ marginLeft: '8px', fontSize: '0.9em', opacity: 0.8 }}>
                        {isMyTurn ? '(Your turn)' : '(Waiting...)'}
                      </span>
                    )}
                  </div>

                  {logExpanded && (
                    <div className="log-backdrop" onClick={() => setLogExpanded(false)} />
                  )}
                  <div className={`game-log ${logExpanded ? 'log-expanded' : ''}`} onClick={() => setLogExpanded(e => !e)}>
                    <h4>Game Log <span className="log-toggle-icon">{logExpanded ? '▲' : '▼'}</span></h4>
                    <div className="log-messages">
                      <AnimatePresence initial={false}>
                        {gameLog.map((msg, i) => (
                          <motion.div
                            key={`${i}-${msg}`}
                            className="log-message"
                            initial={{ opacity: 0, x: -20, height: 0 }}
                            animate={{ opacity: 1, x: 0, height: "auto" }}
                            exit={{ opacity: 0, x: 20, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {msg}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                <div className="players-area">
                  {players.map((player, index) => {
                    // In network mode, show special styling for "you"
                    const isMe = gameMode === 'network' && index === myPlayerIndex
                    const showHand = gameMode === 'network' ? isMe : index === currentPlayerIndex
                    const showControls = gameMode === 'network' ? (isMe && isMyTurn) : index === currentPlayerIndex

                    return (
                      <div
                        key={player.id}
                        className={`player-panel ${index === currentPlayerIndex ? 'active' : ''} ${isMe ? 'my-player' : ''}`}
                      >
                        <div className="player-header">
                          <h3>
                            {player.name}
                            {isMe && <span style={{ marginLeft: '8px', fontSize: '0.8em', opacity: 0.7 }}>(You)</span>}
                          </h3>
                          <div className="player-stats">
                            <span className="hand-count">{player.hand.length} cards</span>
                            <span className="books-count">{player.books.length} books</span>
                          </div>
                        </div>

                        {player.books.length > 0 && (
                          <div className="books">
                            <AnimatePresence>
                              {player.books.map((rank, i) => (
                                <motion.div
                                  key={rank}
                                  className="book"
                                  initial={{ scale: 0, rotateY: 90 }}
                                  animate={{ scale: 1, rotateY: 0 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: i * 0.1
                                  }}
                                >
                                  Book of {rank}s
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}

                        {showHand && (
                          <div className="player-hand">
                            <AnimatePresence>
                              {Array.from(getRankCards(player.hand).entries()).map(([rank, cards], groupIndex) => (
                                <motion.div
                                  key={rank}
                                  className="card-group"
                                  initial={{ opacity: 0, y: 50, rotateY: -90 }}
                                  animate={{ opacity: 1, y: 0, rotateY: 0 }}
                                  exit={{ opacity: 0, scale: 0.8, rotateY: 90 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 200,
                                    damping: 25,
                                    delay: groupIndex * 0.05
                                  }}
                                  whileHover={{
                                    scale: 1.05,
                                    y: -10,
                                    transition: { type: "spring", stiffness: 400, damping: 10 }
                                  }}
                                >
                                  <div className="card">
                                    <div className="card-rank">{rank}</div>
                                    <div className="card-suits">
                                      {cards.map((card, i) => (
                                        <span key={i} className={`suit ${card.suit === '♥' || card.suit === '♦' ? 'red' : 'black'}`}>
                                          {card.suit}
                                        </span>
                                      ))}
                                    </div>
                                    <div className="card-count">×{cards.length}</div>
                                  </div>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}

                        {showControls && !askingRank && (
                          <motion.div
                            className="ask-controls"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <p>Select a rank to ask for:</p>
                            <div className="rank-buttons">
                              {availableRanks.map((rank, i) => (
                                <motion.button
                                  key={rank}
                                  onClick={() => setAskingRank(rank)}
                                  className="rank-button"
                                  initial={{ opacity: 0, scale: 0 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: i * 0.03
                                  }}
                                >
                                  {rank}
                                </motion.button>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {askingRank && showControls && (
                          <motion.div
                            className="target-selection"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                          >
                            <p>Ask for <strong>{askingRank}s</strong> from:</p>
                            <div className="target-buttons">
                              {players.filter((_, i) => i !== myPlayerIndex).map((targetPlayer, i) => (
                                <motion.button
                                  key={targetPlayer.id}
                                  onClick={() => {
                                    const targetIndex = players.indexOf(targetPlayer)
                                    if (gameMode === 'ai') {
                                      askForCardWithAI(targetIndex, askingRank)
                                    } else {
                                      askForCard(targetIndex, askingRank)
                                    }
                                  }}
                                  className="target-button"
                                  disabled={targetPlayer.hand.length === 0 && deck.length === 0}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  whileHover={{ scale: 1.05, y: -2 }}
                                  whileTap={{ scale: 0.95 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20,
                                    delay: i * 0.05
                                  }}
                                >
                                  {targetPlayer.name}
                                </motion.button>
                              ))}
                              <motion.button
                                onClick={() => setAskingRank(null)}
                                className="cancel-button"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{
                                  delay: players.filter((_, i) => i !== myPlayerIndex).length * 0.05 + 0.05
                                }}
                              >
                                Cancel
                              </motion.button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Universal Sidebar for network mode */}
            {gameMode === 'network' && (
              <GameRoomSidebar
                mode="network"
                gameState={{
                  turn: `${currentPlayer.name}'s Turn`,
                  phase: 'Playing'
                }}
                messages={chatMessages}
                historyItems={historyItems}
                onSendMessage={handleSidebarMessage}
                collapsed={sidebar.collapsed}
                onToggleCollapse={sidebar.toggle}
                position={sidebar.position}
                quickReactions={true}
              />
            )}
          </div>
      </div>
    )
  }

  // Game finished
  if (phase === 'finished' && winner) {
    return (
      <div className="gofish-container">
        <NotificationComponent />
        <GameHeader
          gameTitle="Go Fish"
          gameIcon="🎣"
          players={players.map((p, i) => ({ number: i + 1, name: p.name, isConnected: true, avatar: '🎣' }))}
          sessionCode={session?.session_code}
        />
        <div className="gofish-winner-card">
          <div className="gofish-winner-banner">
            <div style={{ fontSize: '4rem', marginBottom: '12px' }}>🎉</div>
            <h2 style={{ color: 'white', fontSize: 'var(--font-size-3xl)', margin: '0 0 8px' }}>{winner.name} wins!</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>with {winner.books.length} books</p>
            <div style={{ marginBottom: '24px' }}>
              {[...players]
                .sort((a, b) => b.books.length - a.books.length)
                .map((player, index) => (
                  <div key={player.id} className={`gofish-score-row ${index === 0 ? 'first' : 'other'}`}>
                    <span>{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                    <span style={{ fontWeight: 600, color: 'white' }}>{player.name}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{player.books.length} books</span>
                  </div>
                ))}
            </div>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={() => {
                setPhase('select')
                setGameMode('select')
                setGameLog([])
                setWinner(null)
                setPlayers([])
              }}
            >
              Play Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Fallback
  return null
}
